import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as UUID } from 'uuid';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import {
  CustomInput,
  CustomSelect,
  CustomTextarea,
  CustomToggle,
} from '../Components/CustomFormElements';
import ItemsPickerModal from '../Components/ItemsPickerModal';
import PageLoader from '../Components/PageLoader';
import {
  ConnectionObjectApiProvider,
  useConnectionObjectApi,
} from '../Services/ConnectionObjectApiProvider';
import { formatBytes, getExtension } from '../utils/fileUtil';
import { hasValidNameFormat, isEmpty } from '../utils/validationUtil';
import ConnectionObjectSchemaAttributes from './ConnectionObjectSchemaAttributes';

const fileColumns = [
  {
    key: 'name',
    header: 'Name',
    widthClass: 'items-picker-col--flex-3',
    render: (item) => {
      const hasLink = Boolean(item.viewLink);
      const handleClick = () => {
        if (hasLink) window.open(item.viewLink, '_blank', 'noreferrer');
      };
      const nameClassName = hasLink
        ? 'items-picker-name items-picker-name--linked'
        : 'items-picker-name';
      return (
        <>
          <div className={nameClassName} title={item.name || ''} onClick={handleClick}>
            {item.name}
          </div>
          <div className="items-picker-id small-font">id: {item.id}</div>
        </>
      );
    },
    searchable: true,
    getSearchValue: (item) => item.name || '',
  },
  {
    key: 'ext',
    header: 'Ext',
    widthClass: 'items-picker-col--flex-1',
    render: (item) => (
      <div className="items-picker-description small-font">
        {item.ext || getExtension(item.name, item.mimeType)}
      </div>
    ),
  },
  {
    key: 'size',
    header: 'Size',
    widthClass: 'items-picker-col--flex-1',
    render: (item) => (
      <div className="items-picker-description small-font">{formatBytes(item.size)}</div>
    ),
  },
  {
    key: 'modified',
    header: 'Modified',
    widthClass: 'items-picker-col--flex-2',
    render: (item) => (
      <div className="items-picker-description small-font">
        {item.createdTime ? new Date(item.createdTime).toLocaleString() : '-'}
      </div>
    ),
  },
];

function ConnectionObjectDetail({ canEdit }) {
  const navigate = useNavigate();
  const { organizationId, connectionId, objectId } = useParams();
  const { showAlert } = useAlert();

  const {
    createConnectionObject,
    updateConnectionObjectById,
    getConnectionObjectById,
    getConnections,
    getFilesByConnection,
  } = useConnectionObjectApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [connections, setConnections] = useState([]);

  const [connectionObjectName, setConnectionObjectName] = useState('');
  const [connectionObjectType, setConnectionObjectType] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [schemaAttributes, setSchemaAttributes] = useState([]);

  // HTTP Object settings (мінімальні додавання)
  const [httpMethod, setHttpMethod] = useState('POST');
  const [httpEndpoint, setHttpEndpoint] = useState('');
  const [httpBodySchema, setHttpBodySchema] = useState('');

  const [httpBodyContentType, setHttpBodyContentType] = useState('application/json');
  const [responsePath, setResponsePath] = useState('');
  const [cacheDurationSeconds, setCacheDurationSeconds] = useState('');

  // GraphQL Object settings
  const [graphqlOperationName, setGraphqlOperationName] = useState('');
  const [graphqlQuerySchema, setGraphqlQuerySchema] = useState('');
  const [graphqlVariables, setGraphqlVariables] = useState('');

  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
  const [savedFileAttribute, setSavedFileAttribute] = useState(null);

  const [connectionObjectFilePermission, setConnectionObjectFilePermission] = useState([]);

  useEffect(() => {
    const func = async () => {
      setLoading(true);
      getConnections().then((connections) => {
        setConnections(connections ?? []);
      });
      try {
        if (objectId !== 'new') {
          const response = await getConnectionObjectById(objectId);

          setConnectionObjectName(response.name);
          setConnectionObjectType(response.type);
          setDescription(response.description || '');
          setIsActive(response.is_active);

          // ⬇️ заміна читання http_object на http_method + endpoint
          if (response.http_method) {
            setHttpMethod(response.http_method || 'POST');
          }
          if (response.http_body_schema) {
            setHttpBodySchema(response.http_body_schema || '');
          }
          if (response.http_body_content_type) {
            setHttpBodyContentType(response.http_body_content_type || 'application/json');
          }
          if (response.response_path) {
            setResponsePath(response.response_path || '');
          }
          if (response.cache_duration_seconds) {
            setCacheDurationSeconds(String(response.cache_duration_seconds));
          }
          if (response.endpoint) {
            setHttpEndpoint(response.endpoint || '');
          }

          // GraphQL settings
          if (response.graphql_operation_name) {
            setGraphqlOperationName(response.graphql_operation_name || '');
          }
          if (response.graphql_query_schema) {
            setGraphqlQuerySchema(response.graphql_query_schema || '');
          }
          if (response.graphql_variables) {
            setGraphqlVariables(response.graphql_variables || '');
          }

          if (response.file_permission) {
            setConnectionObjectFilePermission(response.file_permission || []);
          }

          if (response.file_id) {
            setSavedFileAttribute((prev) => ({
              ...prev,
              file_id: response.file_id,
            }));
          }

          if (response.file_view_link) {
            setSavedFileAttribute((prev) => ({
              ...prev,
              view_link: response.file_view_link,
            }));
          }

          if (response.schema_object && response.schema_object.object_attributes) {
            try {
              const schemaWithKeys = Array.isArray(response.schema_object.object_attributes)
                ? response.schema_object.object_attributes.map((attr) => ({
                    key: attr.key || UUID(),
                    ...attr,
                  }))
                : [];
              setSchemaAttributes(schemaWithKeys);
            } catch (e) {
              console.error('Error parsing schema:', e);
              setSchemaAttributes([]);
            }
          }
        }
      } catch (error) {
        setError('Error fetching connection object data');
        console.error('Error fetching connection object data:', error);
      } finally {
        setLoading(false);
      }
    };
    func();
  }, [objectId]);

  useEffect(() => {
    setIsNew(objectId === 'new');
    setIsDisabled(!canEdit);
  }, [objectId, canEdit]);

  const isGoogleDriveSelected = useMemo(() => {
    if (connectionObjectType !== 'FILE') return false;
    const selected = (connections || []).find((c) => String(c.id) === String(connectionId));
    if (!selected) return false;
    return String(selected.type || '').toLowerCase() === 'google_drive';
  }, [connectionObjectType, connections, connectionId]);

  const attachedDocumentUrl = useMemo(() => {
    if (!savedFileAttribute?.view_link) return null;
    return savedFileAttribute?.view_link;
  }, [savedFileAttribute]);

  const fetchConnectionFiles = useCallback(
    async ({ limit, pageToken, searchQuery } = {}) => {
      try {
        if (!connectionId || !connectionObjectType) {
          return { items: [], nextPageToken: null, hasMore: false };
        }

        const data = await getFilesByConnection(connectionObjectType, connectionId, {
          limit: limit ?? 100,
          pageToken: pageToken ?? null,
          searchQuery: searchQuery ?? null,
        });

        const raw = Array.isArray(data?.items) ? data.items : [];

        const items = raw.map((file) => ({
          id: file?.id,
          name: file?.name,
          mimeType: file?.mime_type,
          size: file?.size,
          createdTime: file?.modified_at,
          viewLink: file?.links?.view,
        }));

        return {
          items,
          nextPageToken: data?.next_page_token ?? null,
          hasMore: Boolean(data?.next_page_token),
        };
      } catch (e) {
        console.error('Error fetching connection files:', e);
        showAlert('fail', 'Failed to load files');
        return { items: [], nextPageToken: null, hasMore: false };
      }
    },
    [connectionId, connectionObjectType, getFilesByConnection, showAlert],
  );

  const handleSaveSelectedFile = useCallback((picked) => {
    const selectedFile = Array.isArray(picked) ? picked[0] : null;
    if (!selectedFile?.id) return;
    setSavedFileAttribute({
      file_id: selectedFile.id,
      view_link: selectedFile.viewLink,
    });
    setIsFilesModalOpen(false);
  }, []);

  const onSave = async () => {
    if (isEmpty(connectionObjectName)) {
      showAlert('fail', 'Connection Object name is required');
      return;
    }
    if (isEmpty(connectionObjectType)) {
      showAlert('fail', 'Connection Object type is required');
      return;
    }
    if (!hasValidNameFormat(connectionObjectName, /^[A-Za-z0-9_-]+$/)) {
      showAlert(
        'fail',
        'Connection Object name can only contain letters, numbers, underscores, and hyphens',
      );
      return;
    }

    setLoading(true);

    try {
      const schemaPayload = {
        object_attributes:
          schemaAttributes.length > 0 ? schemaAttributes.map(({ key, ...rest }) => rest) : [],
      };

      // ⬇️ заміна відправки http_object → на окремі поля http_method / endpoint / http_body_schema
      const httpMethodPayload = connectionObjectType === 'HTTP_OBJECT' ? httpMethod : null;
      const httpEndpointPayload =
        connectionObjectType === 'HTTP_OBJECT' ||
        connectionObjectType === 'GRAPHQL_OBJECT' ||
        connectionObjectType === 'WEBHOOK_OBJECT'
          ? httpEndpoint || null
          : null;
      const httpBodySchemaPayload =
        connectionObjectType === 'HTTP_OBJECT' ? httpBodySchema || null : null;

      const httpBodyContentTypePayload =
        connectionObjectType === 'HTTP_OBJECT' && httpMethod !== 'GET'
          ? httpBodyContentType || null
          : null;
      const responsePathPayload =
        connectionObjectType === 'HTTP_OBJECT' ? responsePath || null : null;
      const parsedCacheDurationSeconds = parseInt(cacheDurationSeconds, 10);
      const cacheDurationSecondsPayload =
        connectionObjectType === 'HTTP_OBJECT' &&
        Number.isFinite(parsedCacheDurationSeconds) &&
        parsedCacheDurationSeconds > 0
          ? parsedCacheDurationSeconds
          : null;

      // GraphQL specific payloads
      const graphqlOperationNamePayload =
        connectionObjectType === 'GRAPHQL_OBJECT' ? graphqlOperationName || null : null;
      const graphqlQuerySchemaPayload =
        connectionObjectType === 'GRAPHQL_OBJECT' ? graphqlQuerySchema || null : null;
      const graphqlVariablesPayload =
        connectionObjectType === 'GRAPHQL_OBJECT' ? graphqlVariables || null : null;

      const commonPayload = {
        name: connectionObjectName,
        description: description || null,
        schema_object: schemaPayload,
        http_method: httpMethodPayload,
        endpoint: httpEndpointPayload,
        http_body_schema: httpBodySchemaPayload,
        http_body_content_type: httpBodyContentTypePayload,
        response_path: responsePathPayload,
        cache_duration_seconds: cacheDurationSecondsPayload,
        graphql_operation_name: graphqlOperationNamePayload,
        graphql_query_schema: graphqlQuerySchemaPayload,
        graphql_variables: graphqlVariablesPayload,
        file_id: savedFileAttribute?.file_id || null,
        file_view_link: savedFileAttribute?.view_link || null,
        is_active: isActive,
        file_permission: connectionObjectFilePermission,
      };

      if (isNew) {
        await createConnectionObject({
          ...commonPayload,
          type: connectionObjectType,
          connection_key: connectionId,
        });
        showAlert('success', 'Connection Object created successfully');
      } else {
        await updateConnectionObjectById(objectId, commonPayload);
        showAlert('success', 'Connection Object updated successfully');
      }
      navigate(`/organizations/${organizationId}/connections/${connectionId}/objects`);
    } catch (error) {
      console.error('Error saving connection object:', error);
      showAlert('fail', 'Failed to save connection object');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/connections/${connectionId}/objects`);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <div className="d-flex justify-between align-center">
            <h2 className="h2-title">{connectionObjectName || 'New Connection Object'}</h2>
          </div>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Connection Object name</div>
                  <div className="small-font">Enter connection object name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="connection-object-name"
                    label="Connection Object Name"
                    placeholder="Enter connection object name"
                    type="text"
                    value={connectionObjectName}
                    onChange={(id, value) => setConnectionObjectName(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Connection Object Type</div>
                  <div className="small-font">Select connection object type</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomSelect
                    id="connection-object-type"
                    label="Connection Object Type"
                    options={[
                      { value: 'LEAD', label: 'Lead' },
                      { value: 'PROCESS', label: 'Process' },
                      { value: 'FILE', label: 'File' },
                      { value: 'BUYER', label: 'Buyer' },
                      { value: 'CARD', label: 'Card' },
                      { value: 'GRAPHQL_OBJECT', label: 'GraphQL Object' },
                      { value: 'HTTP_OBJECT', label: 'HTTP Object' },
                      { value: 'WEBHOOK_OBJECT', label: 'Webhook' },
                      { value: 'FUNCTION', label: 'Function' },
                    ]}
                    value={connectionObjectType}
                    onChange={(value) => setConnectionObjectType(value)}
                    multiSelect={false}
                    disabled={isDisabled || !isNew}
                  />
                </div>
              </div>

              {isGoogleDriveSelected && (
                <>
                  <div className="d-flex align-center justify-between" style={{ marginTop: 10 }}>
                    <div>
                      <div>
                        {attachedDocumentUrl && (
                          <div
                            className="small-font"
                            style={{
                              opacity: 0.9,
                              marginTop: 6,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                            onClick={() => window.open(attachedDocumentUrl, '_blank', 'noreferrer')}
                            title="Open attached document"
                          >
                            Attached document
                          </div>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="primary"
                      size="medium"
                      disabled={isDisabled}
                      onClick={() => setIsFilesModalOpen(true)}
                    >
                      Choose files
                    </Button>
                  </div>

                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Connection File Permission</div>
                      <div className="small-font">Select connection file permission</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomSelect
                        id="connection-file-permission"
                        label="Connection File Permission"
                        options={[
                          { value: 'READ', label: 'Read' },
                          { value: 'UPDATE', label: 'Update' },
                          { value: 'APPEND', label: 'Append' },
                        ]}
                        value={connectionObjectFilePermission}
                        onChange={(value) => setConnectionObjectFilePermission(value)}
                        multiSelect={true}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                </>
              )}

              {connectionObjectType === 'HTTP_OBJECT' && (
                <>
                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">HTTP Method</div>
                      <div className="small-font">Select HTTP method</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomSelect
                        id="http-method"
                        label="HTTP Method"
                        options={[
                          { value: 'GET', label: 'GET' },
                          { value: 'POST', label: 'POST' },
                          { value: 'PUT', label: 'PUT' },
                          { value: 'PATCH', label: 'PATCH' },
                          { value: 'DELETE', label: 'DELETE' },
                        ]}
                        value={httpMethod}
                        onChange={(value) => setHttpMethod(value)}
                        multiSelect={false}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Endpoint address</div>
                      <div className="small-font">Enter HTTP endpoint address</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomInput
                        id="http-endpoint"
                        label="Endpoint address"
                        placeholder="Enter endpoint address"
                        type="text"
                        value={httpEndpoint}
                        onChange={(id, value) => setHttpEndpoint(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                  {httpMethod !== 'GET' && (
                    <div className="input-row input-block d-flex flex-row justify-between">
                      <div className="input-description">
                        <div className="input-title">Body Content Type</div>
                        <div className="small-font">Select how the body is sent</div>
                      </div>
                      <div className="align-wrapper d-flex align-center">
                        <CustomSelect
                          id="http-body-content-type"
                          label="Body Content Type"
                          options={[
                            {
                              value: 'application/json',
                              label: 'application/json',
                            },
                            {
                              value: 'application/x-www-form-urlencoded',
                              label: 'application/x-www-form-urlencoded',
                            },
                          ]}
                          value={httpBodyContentType}
                          onChange={(value) => setHttpBodyContentType(value)}
                          multiSelect={false}
                          disabled={isDisabled}
                        />
                      </div>
                    </div>
                  )}
                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Body Schema</div>
                      <div className="small-font">Enter HTTP body schema</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomTextarea
                        id="http-body-schema"
                        label="Body Schema"
                        placeholder="Enter HTTP body schema"
                        addedText={httpBodySchema}
                        onChange={(value) => setHttpBodySchema(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Response Path</div>
                      <div className="small-font">
                        Dot-path to the value you want from the JSON response. Leave empty to return
                        the whole response.
                      </div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomTextarea
                        id="response-path"
                        label="Response Path"
                        placeholder="e.g. data.items.id"
                        addedText={responsePath}
                        onChange={(value) => setResponsePath(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Cache Duration</div>
                      <div className="small-font">
                        How long, in seconds, a response is reused for identical requests. Leave
                        empty or 0 to disable caching.
                      </div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomInput
                        id="cache-duration-seconds"
                        label="Cache Duration (seconds)"
                        placeholder="e.g. 300"
                        type="text"
                        value={cacheDurationSeconds}
                        onChange={(id, value) =>
                          setCacheDurationSeconds(value.replace(/[^0-9]/g, ''))
                        }
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                </>
              )}

              {connectionObjectType === 'GRAPHQL_OBJECT' && (
                <>
                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Endpoint address</div>
                      <div className="small-font">Enter GraphQL endpoint address</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomInput
                        id="http-endpoint"
                        label="Endpoint address"
                        placeholder="Enter endpoint address"
                        type="text"
                        defaultValue="/graphql"
                        value={httpEndpoint}
                        onChange={(id, value) => setHttpEndpoint(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>

                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Operation Name</div>
                      <div className="small-font">Enter GraphQL operation name</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomInput
                        id="graphql-operation-name"
                        label="Operation Name"
                        placeholder="Enter operation name"
                        type="text"
                        value={graphqlOperationName}
                        onChange={(id, value) => setGraphqlOperationName(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>

                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Query Schema</div>
                      <div className="small-font">Enter GraphQL query schema</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomTextarea
                        id="graphql-query-schema"
                        label="Query Schema"
                        placeholder="Enter GraphQL query schema"
                        addedText={graphqlQuerySchema}
                        onChange={(value) => setGraphqlQuerySchema(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>

                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Variables</div>
                      <div className="small-font">Enter GraphQL variables (JSON format)</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomTextarea
                        id="graphql-variables"
                        label="Variables"
                        placeholder="Enter variables in JSON format"
                        addedText={graphqlVariables}
                        onChange={(value) => setGraphqlVariables(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                </>
              )}

              {connectionObjectType === 'WEBHOOK_OBJECT' && (
                <>
                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">Endpoint address</div>
                      <div className="small-font">Enter Webhook endpoint address</div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomInput
                        id="http-endpoint"
                        label="Endpoint address"
                        placeholder="Enter endpoint address"
                        type="text"
                        defaultValue="/webhook"
                        value={httpEndpoint}
                        onChange={(id, value) => setHttpEndpoint(value)}
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Description</div>
                  <div className="small-font">Enter description (optional)</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomTextarea
                    id="connection-object-description"
                    label="Description"
                    placeholder="Enter description"
                    addedText={description}
                    onChange={(value) => setDescription(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate connection object</div>
                  <div className="small-font">Set connection object to be active or inactive</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status-active"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>

            <ConnectionObjectSchemaAttributes
              schemaAttributes={schemaAttributes}
              setSchemaAttributes={setSchemaAttributes}
              canEdit={canEdit}
              connectionObjectType={connectionObjectType}
            />

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ItemsPickerModal
        isOpen={isFilesModalOpen}
        onClose={() => setIsFilesModalOpen(false)}
        title="Google Drive files"
        disabled={isDisabled}
        fetchItems={fetchConnectionFiles}
        columns={fileColumns}
        multiSelect={false}
        selectedIds={savedFileAttribute?.file_id ? [savedFileAttribute.file_id] : []}
        savedItemId={savedFileAttribute?.file_id || null}
        savedItemUrl={attachedDocumentUrl}
        onSaveSelected={handleSaveSelectedFile}
      />
    </div>
  );
}

function ConnectionObjectDetailWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ConnectionObjectApiProvider>
        <ConnectionObjectDetail canEdit={canEdit} />
      </ConnectionObjectApiProvider>
    </ConfirmationProvider>
  );
}

export default ConnectionObjectDetailWrapper;
